import { useCallback, useEffect, useMemo, useState } from 'react';
import { MarketStatus, LobbyTypeEnum } from '@sportspredict/shared';
import { pickCrowdLockCurrentMarket, sortOpenMarketsByPriorityStable } from '@/utils';
import { ONBOARDING_MARKETS, SWIPE_ONBOARDING_STEPS } from '@/constants';

export function useSwipeStack({
	openMarkets,
	markets,
	userTrades,
	lobby,
	user,
	isSecondChance,
	isPoolingPhaseActive,
	isCurrentSlateLimitReached,
	isCrowdLockMode,
	isUserEliminated,
	isSecondChanceD2,
	secondChanceMarkets,
	userSecondChanceTrades,
	isOnboarding,
	onboardingStepIndex,
	setOnboardingStepIndex,
	handleOnboardingClose,
}: {
	openMarkets: any[];
	markets: any[];
	userTrades: any[];
	lobby: any;
	user: any;
	isSecondChance: boolean;
	isPoolingPhaseActive: boolean;
	isCurrentSlateLimitReached: boolean;
	isCrowdLockMode: boolean;
	isUserEliminated: boolean;
	isSecondChanceD2: boolean;
	secondChanceMarkets: any[];
	userSecondChanceTrades: any[];
	isOnboarding: boolean;
	onboardingStepIndex: number;
	setOnboardingStepIndex: (updater: (prev: number) => number) => void;
	handleOnboardingClose: () => void;
}) {
	const [stack, setStack] = useState(() => {
		const isKickoff = lobby?.game_start_date
			? new Date(lobby.game_start_date) <= new Date()
			: false;
		const isCrowdLock = lobby?.type === LobbyTypeEnum.CrowdLock;

		if (isCrowdLock && !isKickoff && !isSecondChanceD2) {
			const scMarkets = (markets || [])
				.filter((m: any) => m.status === MarketStatus.SecondChance)
				.filter((m: any) => {
					if (!m.id || !userTrades) return true;
					return !userTrades.some((t: any) => String(t.market_id ?? t.marketId) === String(m.id));
				})
				.map((m: any) => ({
					market: m,
					trades: [],
				}));

			if (isUserEliminated && scMarkets.length > 0) {
				return sortOpenMarketsByPriorityStable(scMarkets);
			}
		}

		const baseMarkets = openMarkets || [];
		const filteredMarkets = baseMarkets.filter((item: any) => {
			const mId = item.market?.id;
			if (!mId || !userTrades) return true;
			return !userTrades.some((t: any) => String(t.market_id ?? t.marketId) === String(mId));
		});

		return sortOpenMarketsByPriorityStable(filteredMarkets);
	});
	const [versionById, setVersionById] = useState<Record<string, number>>({});
	const [swipeIntentById, setSwipeIntentById] = useState<Record<string, any>>({});
	const [isMarketLockedById, setIsMarketLockedById] = useState<Record<string, boolean>>({});

	const tradesByMarketId = useMemo(() => {
		const map = new Map();
		(openMarkets || []).forEach((item: any) => {
			if (item?.market?.id) map.set(item.market.id, item.trades ?? []);
		});
		return map;
	}, [openMarkets]);

	const handleMarketLockedChange = useCallback((marketId: any, locked: boolean) => {
		const key = String(marketId);
		setIsMarketLockedById((prev) => {
			if (prev[key] === locked) return prev;
			return { ...prev, [key]: locked };
		});
	}, []);

	const moveToEnd = useCallback((marketId: any) => {
		setStack((prev) => {
			const idx = prev.findIndex((x: any) => x.market.id === marketId);
			if (idx === -1) return prev;
			const item = prev[idx];
			return [...prev.slice(0, idx), ...prev.slice(idx + 1), item];
		});
	}, []);

	const removeFromStack = useCallback((marketId: any) => {
		setStack((prev) => prev.filter((x: any) => x.market.id !== marketId));
	}, []);

	const bumpVersion = useCallback((marketId: any) => {
		setVersionById((prev) => ({
			...prev,
			[marketId]: (prev[marketId] ?? 0) + 1,
		}));
	}, []);

	const hasUserTraded = useCallback(
		(trades: any[]) => {
			if (!trades?.length) return false;
			return trades.some((t: any) => t.user_id === user?.id && t.lobby_id === lobby?.id);
		},
		[user?.id, lobby?.id]
	);

	const registerSwipe = useCallback(
		(dir: string, marketId: any) => {
			if (isOnboarding) {
				if (dir === 'down' || dir === 'up') {
					setOnboardingStepIndex((prev) => {
						const isLast = prev >= SWIPE_ONBOARDING_STEPS.length - 1;
						if (isLast) {
							handleOnboardingClose();
							return 0;
						}
						return prev + 1;
					});
					return;
				}

				if (dir === 'right' || dir === 'left') {
					setOnboardingStepIndex((prev) => {
						const isLast = prev >= SWIPE_ONBOARDING_STEPS.length - 1;
						if (isLast) {
							handleOnboardingClose();
							return 0;
						}
						return prev + 1;
					});
				}

				return;
			}
			if (isCurrentSlateLimitReached) return;

			if (isCrowdLockMode) {
				const item = stack.find((x: any) => x.market.id === marketId);
				if (item && hasUserTraded((item as any).trades)) return;
			}

			if (dir === 'down' || dir === 'up') {
				moveToEnd(marketId);
				bumpVersion(marketId);
				setSwipeIntentById((prev) => {
					if (!prev[marketId]) return prev;
					const copy = { ...prev };
					delete copy[marketId];
					return copy;
				});
				return;
			}

			setSwipeIntentById((prev) => {
				const prevNonce = prev[marketId]?.nonce ?? 0;
				return {
					...prev,
					[marketId]: { dir, nonce: prevNonce + 1 },
				};
			});
		},
		[
			isOnboarding,
			moveToEnd,
			bumpVersion,
			isCurrentSlateLimitReached,
			isCrowdLockMode,
			stack,
			hasUserTraded,
			setOnboardingStepIndex,
			handleOnboardingClose,
		]
	);

	// For D2: load D2 markets into the stack when they arrive from the API
	useEffect(() => {
		if (!isSecondChanceD2 || !secondChanceMarkets?.length) return;

		const d2Stack = secondChanceMarkets
			.filter((m: any) => m.status === MarketStatus.Open)
			.filter((m: any) => {
				if (!userSecondChanceTrades?.length) return true;
				return !userSecondChanceTrades.some(
					(t: any) => String(t.second_chance_market_id) === String(m.id)
				);
			})
			.sort((a: any, b: any) => (a.priority ?? 0) - (b.priority ?? 0))
			.map((m: any) => ({ market: m, trades: [] }));

		// Shallow compare market IDs to avoid resetting stack order on every render
		const currentIds = stack.map((item: any) => item.market?.id).join(',');
		const newIds = d2Stack.map((item: any) => item.market?.id).join(',');

		if (currentIds !== newIds) {
			setStack(d2Stack);
		}
	}, [secondChanceMarkets, isSecondChanceD2, userSecondChanceTrades, stack]);

	useEffect(() => {
		if (isOnboarding) return;
		if (isSecondChanceD2) return; // D2 stack handled above

		let newMarkets = openMarkets || [];

		// Handle pre-kickoff second chance markets visibility
		const isPreKickoff = isCrowdLockMode && isPoolingPhaseActive;
		if (isPreKickoff) {
			const scMarkets = (markets || [])
				.filter((m: any) => m.status === MarketStatus.SecondChance)
				.map((m: any) => ({
					market: m,
					trades: tradesByMarketId.get(m.id) ?? [],
				}));

			if (isUserEliminated && scMarkets.length > 0) {
				newMarkets = scMarkets;
			} else {
				newMarkets = newMarkets.filter((m: any) => m.market?.status === MarketStatus.Open);
			}
		}

		// Filter out markets user has already traded on
		newMarkets = newMarkets.filter((item: any) => {
			const mId = item.market?.id;
			if (!mId || !userTrades) return true;
			return !userTrades.some((t: any) => String(t.market_id ?? t.marketId) === String(mId));
		});

		// Shallow compare market IDs to avoid resetting stack order on every render
		const currentIds = stack.map((item: any) => item.market?.id).join(',');
		const newIds = newMarkets.map((item: any) => item.market?.id).join(',');

		if (currentIds === newIds) {
			// If IDs are the same, it means only market data (like odds) or trades might have changed.
			// We update individual market objects in the existing stack to preserve user's custom order.
			let changed = false;
			const updatedStack = stack.map((oldItem: any) => {
				const newItem = newMarkets.find((n: any) => n.market?.id === oldItem.market?.id);
				const dataChanged =
					newItem &&
					(newItem.market?.current_value !== oldItem.market?.current_value ||
						newItem.market?.status !== oldItem.market?.status ||
						newItem.market?.updated_date !== oldItem.market?.updated_date ||
						newItem.trades !== oldItem.trades);

				if (dataChanged) {
					changed = true;
					return { ...oldItem, market: newItem.market, trades: newItem.trades };
				}
				return oldItem;
			});

			if (changed) {
				setStack(updatedStack);
			}
			return;
		}

		setStack(newMarkets);
		setVersionById({});
		setSwipeIntentById({});
	}, [
		openMarkets,
		isPoolingPhaseActive,
		isCrowdLockMode,
		markets,
		tradesByMarketId,
		userTrades,
		isUserEliminated,
		isSecondChance,
		isOnboarding,
	]);

	const baseVisibleStack = useMemo(() => {
		if (stack.length <= 1) return stack.slice(0, 1);
		if (stack.length === 2) return stack.slice(0, 2);
		return stack.slice(0, 3);
	}, [stack]);

	const crowdLockCurrentMarket = useMemo(() => {
		return pickCrowdLockCurrentMarket(markets, { isSecondChance });
	}, [markets, tradesByMarketId, isSecondChance, user?.id, lobby?.id]);

	const crowdLockVisibleStack = useMemo(() => {
		if (!crowdLockCurrentMarket) return [];

		return [
			{
				market: crowdLockCurrentMarket,
				trades: tradesByMarketId.get(crowdLockCurrentMarket.id) ?? [],
			},
		];
	}, [crowdLockCurrentMarket, tradesByMarketId]);

	const visibleStack = useMemo(() => {
		if (isOnboarding) {
			const market = ONBOARDING_MARKETS[onboardingStepIndex];
			if (!market) return [];
			return [market];
		}

		if (isCrowdLockMode && !isSecondChance && !isPoolingPhaseActive)
			return crowdLockVisibleStack.slice(0, 1);
		return baseVisibleStack;
	}, [
		isOnboarding,
		onboardingStepIndex,
		isCrowdLockMode,
		crowdLockVisibleStack,
		baseVisibleStack,
		isSecondChance,
		isPoolingPhaseActive,
	]);

	return {
		stack,
		tradesByMarketId,
		versionById,
		swipeIntentById,
		isMarketLockedById,
		handleMarketLockedChange,
		moveToEnd,
		removeFromStack,
		bumpVersion,
		hasUserTraded,
		registerSwipe,
		visibleStack,
	};
}
